import { useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { TopDiscountsTodayBaseProps, calculateDiscount, formatDiscount, formatPrice } from './TopDiscountsTodayBase'
import { TagBadge } from '@/components/ui/badge'
import { PriceChart } from './PriceChart'
import { ProjectLogo } from '../UpcomingUnlocks/ProjectLogo'
import { InfoTooltip } from '@/components/ui/info-tooltip'

export const TopDiscountsTodayDesktop = ({ items, loading = false, onItemClick, onBuyClick }: TopDiscountsTodayBaseProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const itemsPerPage = 3
  const maxIndex = Math.max(0, items.length - itemsPerPage)

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - itemsPerPage))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + itemsPerPage))
  }

  const visibleItems = items.slice(currentIndex, currentIndex + itemsPerPage)

  // Show loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <h3 
          className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase mb-3" 
          
        >
          TOP DISCOUNTS TODAY
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">Loading discounts...</p>
        </div>
      </div>
    )
  }

  // Show empty state
  if (items.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <h3 
          className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase mb-3" 
          
        >
          TOP DISCOUNTS TODAY
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">No discounted assets available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-[23px] pt-[23px]">
        <h3 
          className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase" 
          
        >
          <span className="inline-flex items-center gap-2">
            TOP DISCOUNTS TODAY
            <InfoTooltip content="Assets currently priced at the largest discount vs their most recent reported funding-round valuation." ariaLabel="Top Discounts Today info" />
          </span>
        </h3>
        
        {/* Navigation arrows */}
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="w-6 h-6 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous"
          >
            <ArrowLeft className={`w-6 h-6 ${currentIndex === 0 ? 'text-gray-300 dark:text-[rgba(253,253,253,0.3)]' : 'text-[#2d2923] dark:text-[#fdfdfd]'}`} strokeWidth={1.5} />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
            className="w-6 h-6 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next"
          >
            <ArrowRight className={`w-6 h-6 ${currentIndex >= maxIndex ? 'text-gray-300 dark:text-[rgba(253,253,253,0.3)]' : 'text-[#2d2923] dark:text-[#fdfdfd]'}`} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Cards container */}
      <div className="grid grid-cols-3 gap-3 flex-1 min-h-0 px-[23px] pt-[19px] pb-[23px]">
        {visibleItems.map((item) => {
          const discount = calculateDiscount(item)
          return (
            <div
              key={item.order_id}
              className="min-w-0 flex flex-col cursor-pointer"
              onClick={() => onItemClick?.(item)}
            >
              {/* Card content */}
              <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[18px] p-5">
                {/* Header with name and badge */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <ProjectLogo logo={item.logo} color="#949494" alt={item.project_name || ''} />
                    <h4 
                      className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-semibold" 
                    >
                      {item.project_name}
                    </h4>
                  </div>
                  <TagBadge tag={item.tag} />
                </div>

                {/* Discount percentage and price */}
                <div className="flex flex-col gap-1.5">
                  <p 
                    className="leading-9 text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-medium" 
                  >
                    <span className="text-[26px]">-{formatDiscount(discount)} </span>
                    <span className="text-[17px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
                    <span className="text-[11px] leading-3 ml-1.5 font-normal text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                      from last round
                    </span>
                  </p>
                  <p 
                    className="text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" 
                  >
                    {formatPrice(item)}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-1.5">
                  <button
                    className="bg-white dark:bg-[#0a0a0a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] min-w-[96px] flex items-center justify-center px-4 py-2 text-xs leading-4 text-[#2d2923] dark:text-[#fdfdfd] font-medium hover:bg-gray-50 dark:hover:bg-[#101010] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      onItemClick?.(item)
                    }}
                  >
                    More details
                  </button>
                  <button
                    className="bg-[#2d2923] dark:bg-[#fdfdfd] flex-1 flex items-center justify-center px-4 py-2 text-xs leading-4 text-white dark:text-[#2d2923] font-medium hover:bg-[#1a1612] dark:hover:bg-[rgba(253,253,253,0.9)] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      onBuyClick?.(item)
                    }}
                  >
                    BUY
                  </button>
                </div>
              </div>

              {/* Chart section */}
              <div className="bg-[rgba(255,255,255,0.48)] dark:bg-[rgba(28,28,28,0.48)] p-5">
                <PriceChart data={item.priceHistory} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
